import { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import KpiGrid from '../components/KpiGrid'
import DataTable, { type Column } from '../components/DataTable'
import ErrorAlert from '../components/ErrorAlert'
import type { Advisor, ConversationListResponse, ConversationSummary } from '../types/portal'

/**
 * "Métricas" — indicadores del equipo y atención por asesor (supervisión).
 *
 * Se arma con tres endpoints que ya existen: `/dashboard` (agregados),
 * `/advisors` (carga y disponibilidad) y `/conversations` (para el desglose por
 * resultado y el estado de las ventanas de 24 h).
 *
 * La latencia que se muestra es la que vive el CLIENTE: cuánto espera la
 * respuesta a sus documentos, p50/p90/máximo de los últimos días. La escribe el
 * compositor de la conversación y la calcula CloudWatch (`respuesta_documentos`
 * de `/dashboard`).
 *
 * Lo que NO está acá y el diseño sí pide: invocaciones Lambda, DLQ y alertas de
 * CloudWatch. Eso es la fase 3 (Observabilidad), y no se inventan números en el
 * frontend para llenar tarjetas.
 */

interface DashboardData {
  conversations?: { active: number; total: number }
  documents?: { pending: number; approved: number; rejected: number }
  outreach?: { daily_sent: number; daily_limit: number }
  model_calls?: { today: number; budget_tokens: number }
  respuesta_documentos?: {
    p50: number | null
    p90: number | null
    max: number | null
    n: number
    dias: number
  }
}

/** Sin datos es `null`, no cero: un cero diría que contestamos al instante. */
function segundos(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${v.toFixed(1)} s`
}

const OUTCOME_LABEL: Record<string, string> = {
  interesado: 'Interesado',
  no_contesta: 'No contesta',
  no_califica: 'No califica',
  otro: 'Otro',
}

const ACTIVOS = ['ACTIVE', 'OPEN', 'HUMAN_REQUIRED', 'IN_REVIEW']

interface FilaAsesor extends Advisor {
  resueltas: number
  ventanaEnRiesgo: number
}

export default function Metrics() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [advisors, setAdvisors] = useState<Advisor[]>([])
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    // Las tres llamadas en paralelo y con `allSettled`: si `/dashboard` falla no
    // hay razón para esconder también la tabla por asesor.
    const [d, a, c] = await Promise.allSettled([
      apiClient.get<DashboardData>('/dashboard'),
      apiClient.get<{ advisors: Advisor[] }>('/advisors'),
      apiClient.get<ConversationListResponse>('/conversations?limit=100'),
    ])

    if (d.status === 'fulfilled') setDashboard(d.value.data)
    if (a.status === 'fulfilled') setAdvisors(a.value.data.advisors ?? [])
    if (c.status === 'fulfilled') setConversations(c.value.data.conversations ?? [])

    const fallo = [d, a, c].find((r) => r.status === 'rejected')
    if (fallo && fallo.status === 'rejected') {
      setError(errorMessage(fallo.reason, 'Algunos indicadores no se pudieron cargar.'))
    }
    setLoading(false)
  }, [])

  useEffect(() => { void fetch() }, [fetch])

  // ── Derivados ─────────────────────────────────────────────────────────────

  const hoy = new Date().toISOString().slice(0, 10)

  const resueltasHoy = useMemo(
    () => conversations.filter((c) => (c.resolved_at ?? '').slice(0, 10) === hoy).length,
    [conversations, hoy],
  )

  const ventanasEnRiesgo = useMemo(
    () => conversations.filter(
      (c) => ACTIVOS.includes(c.status) && !c.window.can_send_free_text,
    ).length,
    [conversations],
  )

  const sinAsignar = useMemo(
    () => conversations.filter((c) => !c.assigned_to && ACTIVOS.includes(c.status)).length,
    [conversations],
  )

  const porResultado = useMemo(() => {
    const cuenta = new Map<string, number>()
    for (const c of conversations) {
      const clave = c.case_outcome || 'sin_tipificar'
      cuenta.set(clave, (cuenta.get(clave) ?? 0) + 1)
    }
    return [...cuenta.entries()].sort((a, b) => b[1] - a[1])
  }, [conversations])

  // La carga por asesor sale de `/advisors` (misma cuenta que usa el
  // round-robin); las resueltas de hoy y las ventanas en riesgo se cruzan con
  // el listado de conversaciones.
  const filas = useMemo<FilaAsesor[]>(() => advisors.map((a) => {
    const suyas = conversations.filter((c) => c.assigned_to === a.email)
    return {
      ...a,
      resueltas: suyas.filter((c) => (c.resolved_at ?? '').slice(0, 10) === hoy).length,
      ventanaEnRiesgo: suyas.filter(
        (c) => ACTIVOS.includes(c.status) && !c.window.can_send_free_text,
      ).length,
    }
  }), [advisors, conversations, hoy])

  const columnas: Column<FilaAsesor>[] = [
    {
      key: 'asesor',
      header: 'Asesor',
      cell: (a) => <span className="font-semibold">{a.name}</span>,
      sub: (a) => a.email,
    },
    {
      key: 'estado',
      header: 'Disponibilidad',
      cell: (a) => (
        <span className={a.available ? 'badge-green' : 'badge-amber'}>
          {a.available ? 'Recibiendo' : 'En pausa'}
        </span>
      ),
    },
    {
      key: 'activas',
      header: 'Activas',
      cell: (a) => <span className="font-mono">{a.active_conversations}</span>,
    },
    {
      key: 'resueltas',
      header: 'Resueltas hoy',
      cell: (a) => <span className="font-mono">{a.resueltas}</span>,
    },
    {
      key: 'riesgo',
      header: 'Ventana cerrada',
      cell: (a) => a.ventanaEnRiesgo > 0
        ? <span className="badge-amber">{a.ventanaEnRiesgo}</span>
        : <span className="text-ink-faint">—</span>,
    },
    {
      key: 'rotacion',
      header: 'Rotación',
      align: 'right',
      cell: (a) => a.rotation_position
        ? <span className="font-mono">{a.rotation_position}º</span>
        : <span className="text-ink-faint">Fuera</span>,
    },
  ]

  const docs = dashboard?.documents
  const tokens = dashboard?.model_calls
  const respuesta = dashboard?.respuesta_documentos
  const respuestaHint = respuesta?.n
    ? `${respuesta.n} respuestas · últimos ${respuesta.dias} días`
    : 'Sin datos todavía'

  return (
    <div className="flex flex-col gap-5">
      {error && <ErrorAlert message={error} onRetry={() => void fetch()} />}

      <KpiGrid
        kpis={[
          { label: 'Conversaciones activas', value: dashboard?.conversations?.active ?? '—',
            hint: `${dashboard?.conversations?.total ?? '—'} en total` },
          { label: 'Resueltas hoy', value: resueltasHoy },
          { label: 'Sin asignar', value: sinAsignar,
            hint: 'Esperando a un asesor',
            tone: sinAsignar > 0 ? 'warn' : 'default' },
          { label: 'Ventana cerrada', value: ventanasEnRiesgo,
            hint: 'Activas que ya solo aceptan plantilla',
            tone: ventanasEnRiesgo > 0 ? 'danger' : 'default' },
        ]}
      />

      <KpiGrid
        kpis={[
          { label: 'Documentos por revisar', value: docs?.pending ?? '—',
            tone: (docs?.pending ?? 0) > 0 ? 'warn' : 'default' },
          { label: 'Documentos aprobados', value: docs?.approved ?? '—' },
          { label: 'Documentos rechazados', value: docs?.rejected ?? '—' },
          { label: 'Tokens del día', value: (tokens?.today ?? 0).toLocaleString('es-PE'),
            hint: `de ${(tokens?.budget_tokens ?? 0).toLocaleString('es-PE')} de presupuesto` },
        ]}
      />

      <KpiGrid
        columns={3}
        kpis={[
          { label: 'Respuesta a documentos · p50', value: segundos(respuesta?.p50),
            hint: respuestaHint },
          { label: 'Respuesta a documentos · p90', value: segundos(respuesta?.p90),
            hint: 'Desde que llega el primer archivo del turno' },
          { label: 'Respuesta a documentos · máx', value: segundos(respuesta?.max),
            hint: 'La espera más larga de la ventana' },
        ]}
      />

      <DataTable
        title="Atención por asesor"
        subtitle="Carga actual, cierres de hoy y posición en la rotación round-robin"
        columns={columnas}
        rows={filas}
        rowKey={(a) => a.email}
        loading={loading}
        emptyTitle="No hay asesores en el grupo Asesor"
        emptyMessage="Un admin los agrega desde Administración › Usuarios."
        action={<button className="btn-ghost" onClick={() => void fetch()}>Actualizar</button>}
      />

      <div className="card p-5">
        <h2 className="text-[14px] font-extrabold text-ink">Resultado del caso</h2>
        <p className="mt-px text-micro text-ink-muted">
          Sobre las últimas {conversations.length} conversaciones. `case_outcome` lo escribe el
          árbol de decisión del bot; el resultado que tipifica el asesor al cerrar se guarda
          aparte en `resolution_outcome`.
        </p>
        {porResultado.length === 0 ? (
          <p className="mt-4 text-body text-ink-faint">Todavía no hay conversaciones cargadas.</p>
        ) : (
          <div className="mt-4 flex flex-col gap-2">
            {porResultado.map(([clave, total]) => {
              const pct = Math.round((total / conversations.length) * 100)
              return (
                <div key={clave} className="flex items-center gap-3">
                  <span className="w-40 shrink-0 truncate text-body text-ink-muted">
                    {OUTCOME_LABEL[clave] ?? (clave === 'sin_tipificar' ? 'Sin tipificar' : clave)}
                  </span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-subtle">
                    <div className="h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-16 shrink-0 text-right font-mono text-micro text-ink">
                    {total} · {pct}%
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
